import { watch } from 'vue'

import { ShopifyApi } from '../api/ShopifyApi'
import { OAuthCallbackFlow } from '../flows/OAuthCallbackFlow'
import type { IntegrationQueryCallbackRuntime } from '../flows/queryCallbacks'
import { ShopifyInstallFlow } from '../flows/ShopifyInstallFlow'
import type { CallbackNotice } from '../models/callback.types'
import { completeShopifyInstall } from '../service/completeShopifyInstall'
import { clearConnectIntent, hasConnectIntent } from '../service/connectIntent'
import { refreshAll } from '../service/integrationService'

import { useIntegrations } from './useIntegrations'

import { useAccount, useWorkspace } from '@/features/account'
import { useToast } from '@/shared/ui'

export type IntegrationCallbackTranslator = (
  key: string,
  params?: Record<string, unknown>,
) => string

export function useIntegrationCallbackRuntime(
  translate: IntegrationCallbackTranslator,
): IntegrationQueryCallbackRuntime {
  const account = useAccount()
  const workspace = useWorkspace()
  const integrations = useIntegrations()
  const toast = useToast()

  const notify = (notice: CallbackNotice): void => {
    const message = translate(notice.key, notice.params)
    toast[notice.tone](message)
  }
  const reloadAccount = () => account.loadSelectedAccountContext(true)

  return {
    notify,
    whenContextReady: () =>
      account.accountId.value !== undefined &&
      workspace.workspaceId.value !== undefined &&
      integrations.isReady.value &&
      integrations.connectableIntegrations.value !== null
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            const stop = watch(
              [
                account.accountId,
                workspace.workspaceId,
                integrations.isReady,
                integrations.connectableIntegrations,
              ],
              ([accountId, workspaceId, isReady, connectable]) => {
                if (
                  accountId === undefined ||
                  workspaceId === undefined ||
                  !isReady ||
                  connectable === null
                ) {
                  return
                }
                stop()
                resolve()
              },
            )
          }),
    shopify: new ShopifyInstallFlow({
      complete: completeShopifyInstall,
      installStore: (command) => new ShopifyApi().installStore(command),
      reloadAccount,
      refreshIntegrations: () => refreshAll(true),
      notify,
    }),
    oauth: new OAuthCallbackFlow({
      reloadAccount,
      // The onboarding decision is workspace-scoped: another workspace in the same account may
      // already have Shopify without making this workspace a reconnect.
      hasShopifyIntegration: () => integrations.isConnected('shopify'),
      notify,
      connectIntent: { pending: () => hasConnectIntent(), clear: clearConnectIntent },
    }),
  }
}
